// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState } from 'react'
import { formatUsDateLocal } from '../../api/format'
import { Loader2, AlertCircle, Plus, RefreshCcw, X, Copy, Check } from 'lucide-react'
import * as api from '../../api/client'
import { SCOPE_OPTIONS } from '../../components/admin/SiteOfCareScopeSection'
import type { SiteOfCareScopeMode } from '../../api/siteOfCare'
import ManufacturerDrugPicker, { type DrugGrant } from '../../components/admin/ManufacturerDrugPicker'
import { useAuth } from '../../auth/AuthContext'
import { usePharmacy } from '../../auth/PharmacyContext'
import { SectionCard } from './AdminLayout'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'

interface Invitation {
  id: string
  email: string
  display_name: string | null
  created_at: string
  expires_at: string
  accepted_at: string | null
  revoked_at: string | null
  revoke_reason: string | null
  invited_by_id: string | null
  invited_by_name: string | null
  role_ids_json: string
  status: 'pending' | 'accepted' | 'revoked' | 'expired'
}

interface Role {
  id: string
  name: string
  description: string | null
  is_system: boolean
  display_name: string | null
}

interface EmailPreview { subject: string; body: string; audience: string }
interface CreateResponse { id: string; email: string; expires_at: string; bootstrap_link: string; email_preview?: EmailPreview }

const FILTERS: { key: string; label: string }[] = [
  { key: '',         label: 'All' },
  { key: 'pending',  label: 'Pending' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'revoked',  label: 'Revoked' },
  { key: 'expired',  label: 'Expired' },
]

const STATUS_TONE: Record<string, { bg: string; fg: string }> = {
  pending:  { bg: '#dbeafe', fg: '#1e40af' },
  accepted: { bg: '#dcfce7', fg: '#166534' },
  revoked:  { bg: '#fee2e2', fg: '#991b1b' },
  expired:  { bg: '#f3f4f6', fg: '#4b5563' },
}

// D-UI-2: what each column shows, for its header funnel.
const INVITATION_FUNNELS: FunnelColumn<Invitation>[] = [
  { key: 'email',   label: 'Email',      value: i => i.email },
  { key: 'status',  label: 'Status',     value: i => i.status.toUpperCase() },
  { key: 'invited', label: 'Invited',    value: i => i.created_at },
  { key: 'expires', label: 'Expires',    value: i => i.expires_at },
  { key: 'by',      label: 'Invited by', value: i => i.invited_by_name },
]

export default function InvitationsTab() {
  const { me } = useAuth()
  const canInvite = Boolean(me?.permissions?.includes('admin.users.invite'))

  const [items, setItems] = useState<Invitation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<string>('pending')
  const [showInviteForm, setShowInviteForm] = useState(false)
  const [bootstrapLink, setBootstrapLink] = useState<{ email: string; link: string; preview?: EmailPreview } | null>(null)

  async function reload() {
    setLoading(true); setError(null)
    try {
      const qs = filter ? `?status=${filter}` : ''
      const r = await api.apiFetch(`/invitations${qs}`, { credentials: 'include' })
      if (!r.ok) throw await api.parseApiError(r)
      const data = await r.json()
      setItems(data.items)
    } catch (e: any) {
      setError(e?.body?.message || e?.message || 'Failed to load invitations.')
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { reload() }, [filter])

  // The header funnels filter the whole loaded list.
  const fx = useTableFunnels(items, INVITATION_FUNNELS)

  async function handleResend(id: string) {
    try {
      const r = await api.apiFetch(`/invitations/${id}/resend`, {
        method: 'POST', credentials: 'include',
      })
      if (!r.ok) throw await api.parseApiError(r)
      const data = await r.json()
      setBootstrapLink({ email: items.find(i => i.id === id)?.email ?? '', link: data.bootstrap_link, preview: data.email_preview })
      reload()
    } catch (e: any) {
      setError(e?.body?.message || e?.message || 'Resend failed.')
    }
  }

  async function handleRevoke(id: string) {
    const reason = window.prompt('Revoke this invitation? Optional reason:')
    if (reason === null) return
    try {
      const r = await api.apiFetch(`/invitations/${id}/revoke`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason || undefined }),
      })
      if (!r.ok) throw await api.parseApiError(r)
      reload()
    } catch (e: any) {
      setError(e?.body?.message || e?.message || 'Revoke failed.')
    }
  }

  return (
    <div>
      <div style={toolbarStyle}>
        <div style={chipRowStyle}>
          {FILTERS.map(f => (
            <button
              key={f.key || 'all'}
              onClick={() => setFilter(f.key)}
              style={{
                ...chipStyle,
                ...(filter === f.key ? chipActiveStyle : null),
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
        {canInvite && (
          <button onClick={() => setShowInviteForm(s => !s)} style={primaryButtonStyle}>
            <Plus size={14} aria-hidden="true" /> Invite user
          </button>
        )}
      </div>

      {showInviteForm && (
        <InviteForm
          onCreated={(resp, email) => {
            setBootstrapLink({ email, link: resp.bootstrap_link, preview: resp.email_preview })
            setShowInviteForm(false)
            reload()
          }}
          onCancel={() => setShowInviteForm(false)}
        />
      )}

      {bootstrapLink && (
        <BootstrapLinkPanel
          email={bootstrapLink.email}
          link={bootstrapLink.link}
          preview={bootstrapLink.preview}
          onClose={() => setBootstrapLink(null)}
        />
      )}

      <SectionCard>
        {loading && (
          <div style={emptyStyle}>
            <Loader2 size={18} className="spin" aria-hidden="true" />
            <span>Loading invitations…</span>
          </div>
        )}
        {error && (
          <div role="alert" style={errorStyle}>
            <AlertCircle size={16} aria-hidden="true" /> {error}
          </div>
        )}
        {!loading && !error && items.length === 0 && (
          <div style={emptyStyle}>No invitations {filter ? `with status "${filter}"` : 'yet'}.</div>
        )}
        {!loading && !error && items.length > 0 && (
          <>
          {fx.chips}
          <table {...fx.tableProps} style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Email {fx.funnel('email')}</th>
                <th style={thStyle}>Status {fx.funnel('status')}</th>
                <th style={thStyle}>Invited {fx.funnel('invited')}</th>
                <th style={thStyle}>Expires {fx.funnel('expires')}</th>
                <th style={thStyle}>Invited by {fx.funnel('by')}</th>
                <th data-no-funnel="row actions" style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {fx.rows.map(i => {
                const tone = STATUS_TONE[i.status] ?? STATUS_TONE.expired
                return (
                  <tr key={i.id} style={trStyle}>
                    <td style={tdStyle}>
                      <div className="email-text" style={{ fontWeight: 500 }}>{i.email}</div>
                      {i.display_name && (
                        <div style={{ fontSize: 11, color: 'var(--color-text-mute)' }}>{i.display_name}</div>
                      )}
                    </td>
                    <td style={tdStyle}>
                      <span style={{ ...statusPillStyle, background: tone.bg, color: tone.fg }}>
                        {i.status.toUpperCase()}
                      </span>
                    </td>
                    <td style={tdStyle}>{relTime(i.created_at)}</td>
                    <td style={tdStyle}>{relTime(i.expires_at)}</td>
                    <td style={tdStyle}>{i.invited_by_name ?? '—'}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      {i.status === 'pending' && canInvite && (
                        <>
                          <button onClick={() => handleResend(i.id)} style={ghostBtnStyle} title="Resend with new link">
                            <RefreshCcw size={13} aria-hidden="true" />
                            <span>Resend</span>
                          </button>
                          <button onClick={() => handleRevoke(i.id)} style={dangerBtnStyle} title="Revoke">
                            <X size={13} aria-hidden="true" />
                            <span>Revoke</span>
                          </button>
                        </>
                      )}
                      {i.status === 'expired' && canInvite && (
                        <button onClick={() => handleResend(i.id)} style={ghostBtnStyle} title="Resend with new link">
                          <RefreshCcw size={13} aria-hidden="true" /> <span>Resend</span>
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          </>
        )}
      </SectionCard>
    </div>
  )
}

// ----- Invite form -----

function InviteForm({
  onCreated, onCancel,
}: { onCreated: (resp: CreateResponse, email: string) => void; onCancel: () => void }) {
  const [roles, setRoles] = useState<Role[]>([])
  const { activePharmacy } = usePharmacy()
  const [rolesLoading, setRolesLoading] = useState(true)
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(new Set())
  const [requireMfa, setRequireMfa] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // External-provider linkage (shown when the "Provider" role is selected)
  const [linkMode, setLinkMode] = useState<'existing' | 'new'>('existing')
  const [prescSearch, setPrescSearch] = useState('')
  const [prescResults, setPrescResults] = useState<api.PrescriberListItem[]>([])
  const [selectedPresc, setSelectedPresc] = useState<api.PrescriberListItem | null>(null)
  const [newNpi, setNewNpi] = useState('')
  const [newPrescName, setNewPrescName] = useState('')
  const [newSpecialty, setNewSpecialty] = useState('')

  const providerRoleSelected = roles.some(r => selectedRoles.has(r.id) && r.name === 'Provider')

  // Manufacturer drug grant (shown when the "Manufacturer" role is selected). D-MFR-1.
  // The picker itself lives in ManufacturerDrugPicker so this form and the user detail
  // panel cannot drift — the same clicks must produce the same grant on both.
  const [drugGrants, setDrugGrants] = useState<DrugGrant[]>([])

  const manufacturerRoleSelected = roles.some(r => selectedRoles.has(r.id) && r.name === 'Manufacturer')
  // D-SOC-3: site-of-care scope for external accounts (Provider / Provider Nurse / Facility Staff).
  const scopeRoleSelected = roles.some(r => selectedRoles.has(r.id) && (r.name === 'Provider' || r.name === 'Provider Nurse' || r.name === 'Facility Staff'))
  const facilityRoleSelected = roles.some(r => selectedRoles.has(r.id) && r.name === 'Facility Staff')
  const [socScope, setSocScope] = useState<SiteOfCareScopeMode>('all')
  const [socFacilities, setSocFacilities] = useState<string[]>([])
  const [facilityDir, setFacilityDir] = useState<api.FacilityRow[]>([])
  const [facQ, setFacQ] = useState('')
  useEffect(() => { if (facilityRoleSelected && socScope === 'all') setSocScope('facility') }, [facilityRoleSelected])   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!scopeRoleSelected || socScope !== 'facility' || facilityDir.length) return
    // PICKER over the whole directory — listFacilities is paged and defaults to 50, which
    // would hide facilities from the invite's facility-scope list without saying so.
    api.listFacilities(undefined, true, { page: 1, pageSize: 200 }).then(d => setFacilityDir(d.items)).catch(() => {})
  }, [scopeRoleSelected, socScope, facilityDir.length])

  useEffect(() => {
    if (linkMode !== 'existing' || prescSearch.trim().length < 2) { setPrescResults([]); return }
    let cancelled = false
    const t = setTimeout(() => {
      api.listPrescribers(prescSearch.trim())
        .then(d => { if (!cancelled) setPrescResults(d.items.slice(0, 8)) })
        .catch(() => { if (!cancelled) setPrescResults([]) })
    }, 250)
    return () => { cancelled = true; clearTimeout(t) }
  }, [prescSearch, linkMode])

  useEffect(() => {
    api.apiFetch(`/roles`, { credentials: 'include' })
      .then(async r => {
        if (!r.ok) throw await api.parseApiError(r)
        return r.json()
      })
      .then((d: { items: Role[] }) => setRoles(d.items))
      .catch(() => setRoles([]))
      .finally(() => setRolesLoading(false))
  }, [])

  function toggleRole(id: string) {
    setSelectedRoles(s => {
      const next = new Set(s)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (submitting) return
    setError(null)

    if (!email.trim()) { setError('Email is required.'); return }
    if (selectedRoles.size === 0) { setError('Pick at least one role.'); return }
    // An empty grant and a missing grant are indistinguishable downstream, and one of
    // them would have to mean "every drug". Refuse to create that ambiguity here.
    if (manufacturerRoleSelected && drugGrants.length === 0) {
      setError('Pick at least one drug or manufacturer for this account to see.'); return
    }

    // Provider-link validation + payload
    let prescriberId: string | undefined
    let newPrescriber: { npi: string; display_name?: string; specialty?: string } | undefined
    if (providerRoleSelected) {
      if (!activePharmacy) {
        setError('Open the pharmacy first (dial into it) so the provider is linked to it. Inviting from the SaaS view would create an unbound account.')
        return
      }
      if (linkMode === 'existing') {
        if (!selectedPresc) { setError('Pick the prescriber this provider is, or switch to "Create new".'); return }
        prescriberId = selectedPresc.id
      } else {
        if (!newNpi.trim()) { setError('NPI is required to create a prescriber.'); return }
        newPrescriber = {
          npi: newNpi.trim(),
          display_name: newPrescName.trim() || undefined,
          specialty: newSpecialty.trim() || undefined,
        }
      }
    }

    setSubmitting(true)
    try {
      const r = await api.apiFetch(`/invitations`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          display_name: displayName.trim() || undefined,
          role_ids: Array.from(selectedRoles),
          pharmacy_id: activePharmacy?.id,
          prescriber_id: prescriberId,
          new_prescriber: newPrescriber,
          require_mfa: requireMfa,
          manufacturer_scope: manufacturerRoleSelected ? drugGrants : undefined,
          site_of_care_scope: scopeRoleSelected ? socScope : undefined,
          facility_scope: scopeRoleSelected && socScope === 'facility' ? socFacilities : undefined,
        }),
      })
      if (!r.ok) throw await api.parseApiError(r)
      const resp: CreateResponse = await r.json()
      onCreated(resp, email.trim())
    } catch (e: any) {
      const code = e?.body?.error
      const msg =
        code === 'email_already_registered' ? 'An account with that email already exists.' :
        code === 'invite_already_pending'   ? 'There is already a pending invitation for that email.' :
        e?.body?.message || 'Could not create invitation.'
      setError(msg)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={submit} style={inviteFormStyle}>
      <div style={inviteFormGridStyle}>
        <div>
          <label style={labelStyle} htmlFor="inv-email">Email</label>
          <input
            id="inv-email"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            disabled={submitting}
            required
            style={inputStyle}
            placeholder="user@example.com"
          />
        </div>
        <div>
          <label style={labelStyle} htmlFor="inv-name">Display name (optional)</label>
          <input
            id="inv-name"
            type="text"
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
            disabled={submitting}
            style={inputStyle}
            placeholder="Leave blank to let the user choose"
          />
        </div>
      </div>

      <label style={{ ...labelStyle, marginTop: 14 }}>Roles to assign</label>
      {rolesLoading ? (
        <div style={{ fontSize: 13, color: 'var(--color-text-mute)' }}>Loading roles…</div>
      ) : (
        <div style={roleChipsStyle}>
          {roles.map(role => (
            <label key={role.id} style={{
              ...roleChipStyle,
              ...(selectedRoles.has(role.id) ? roleChipActiveStyle : null),
            }}>
              <input
                type="checkbox"
                checked={selectedRoles.has(role.id)}
                onChange={() => toggleRole(role.id)}
                disabled={submitting}
                style={{ marginRight: 6 }}
              />
              {role.display_name || role.name}
            </label>
          ))}
        </div>
      )}

      {manufacturerRoleSelected && (
        <div style={{ marginTop: 16, padding: 14, border: '1px solid var(--color-border)', borderRadius: 10, background: 'var(--color-surface-2, #f9fafb)' }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Choose the drugs this manufacturer can see</div>
          <div style={{ fontSize: 12, color: 'var(--color-text-mute)', marginBottom: 12 }}>
            Search by NDC, drug name, brand, or manufacturer. Selecting a manufacturer heading grants
            every drug under it. Patient information is masked for this role unless it is allowed on
            the account after the invitation is accepted.
          </div>
          <ManufacturerDrugPicker value={drugGrants} onChange={setDrugGrants} />
        </div>
      )}

      {scopeRoleSelected && (
        <div style={{ marginTop: 16, padding: 14, border: '1px solid var(--color-border)', borderRadius: 10, background: 'var(--color-surface-2, #f9fafb)' }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>What can this account see?</div>
          <div style={{ fontSize: 12, color: 'var(--color-text-mute)', marginBottom: 10 }}>
            On top of the prescriber (NPI) link: everything for that NPI, only facility residents, or only patients at home. A Facility Staff account with no NPI sees its facilities only.
          </div>
          <div style={{ display: 'grid', gap: 8 }}>
            {SCOPE_OPTIONS.map(o => (
              <label key={o.value} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, cursor: 'pointer' }}>
                <input type="radio" name="invite-soc-scope" checked={socScope === o.value} onChange={() => setSocScope(o.value)} style={{ marginTop: 3 }} />
                <span><span style={{ fontWeight: 600 }}>{o.label}</span><br /><span style={{ fontSize: 12, color: 'var(--color-text-mute)' }}>{o.help}</span></span>
              </label>
            ))}
          </div>
          {socScope === 'facility' && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 6 }}>Facilities ({socFacilities.length} selected)</div>
              <input value={facQ} onChange={e => setFacQ(e.target.value)} placeholder="Search facilities by name, city or zip…" aria-label="Search facilities"
                style={{ width: '100%', height: 30, padding: '0 10px', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 13, marginBottom: 6, boxSizing: 'border-box' }} />
              <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 8, background: '#fff' }}>
                {facilityDir.filter(f => { const n = facQ.trim().toLowerCase(); return !n || f.name.toLowerCase().includes(n) || (f.city ?? '').toLowerCase().includes(n) || (f.zip_code ?? '').includes(n) }).map(f => (
                  <label key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', borderBottom: '1px solid var(--color-border)', fontSize: 13, cursor: 'pointer' }}>
                    <input type="checkbox" checked={socFacilities.includes(f.id)} onChange={() => setSocFacilities(s => s.includes(f.id) ? s.filter(x => x !== f.id) : [...s, f.id])} />
                    <span style={{ flex: 1 }}><span style={{ fontWeight: 600 }}>{f.name}</span><span style={{ color: 'var(--color-text-mute)', marginLeft: 8, fontSize: 12 }}>{[f.city, f.zip_code].filter(Boolean).join(' ')}</span></span>
                  </label>
                ))}
                {facilityDir.length === 0 && <div style={{ padding: 10, fontSize: 12.5, color: 'var(--color-text-mute)' }}>No facilities in the Site of Care directory yet.</div>}
              </div>
            </div>
          )}
        </div>
      )}

      {providerRoleSelected && (
        <div style={{ marginTop: 16, padding: 14, border: '1px solid var(--color-border)', borderRadius: 10, background: 'var(--color-surface-2, #f9fafb)' }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Link this provider to a prescriber</div>
          <div style={{ fontSize: 12, color: 'var(--color-text-mute)', marginBottom: 12 }}>
            This is the anchor for what the provider sees — they’ll only see this prescriber’s prescriptions in their portal.
          </div>

          <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
            <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="radio" checked={linkMode === 'existing'} onChange={() => setLinkMode('existing')} disabled={submitting} />
              Link existing
            </label>
            <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="radio" checked={linkMode === 'new'} onChange={() => setLinkMode('new')} disabled={submitting} />
              Create new
            </label>
          </div>

          {linkMode === 'existing' ? (
            <div>
              {selectedPresc ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1px solid var(--color-border)', borderRadius: 8, background: 'var(--color-surface)' }}>
                  <div style={{ fontSize: 13 }}>
                    <div style={{ fontWeight: 600 }}>{selectedPresc.display_name}</div>
                    <div style={{ color: 'var(--color-text-mute)' }}>NPI {selectedPresc.npi}{selectedPresc.specialty ? ` · ${selectedPresc.specialty}` : ''}</div>
                  </div>
                  <button type="button" onClick={() => { setSelectedPresc(null); setPrescSearch('') }} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--color-text-mute)', cursor: 'pointer', fontSize: 13 }}>Change</button>
                </div>
              ) : (
                <>
                  <input
                    type="text" value={prescSearch} onChange={e => setPrescSearch(e.target.value)}
                    disabled={submitting} style={inputStyle} placeholder="Search prescribers by name or NPI…"
                  />
                  {prescResults.length > 0 && (
                    <div style={{ marginTop: 6, border: '1px solid var(--color-border)', borderRadius: 8, overflow: 'hidden' }}>
                      {prescResults.map(p => (
                        <button key={p.id} type="button" onClick={() => { setSelectedPresc(p); setPrescResults([]) }}
                          style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', border: 'none', borderBottom: '1px solid var(--color-border)', background: 'var(--color-surface)', cursor: 'pointer', fontSize: 13 }}>
                          <span style={{ fontWeight: 600 }}>{p.display_name}</span>
                          <span style={{ color: 'var(--color-text-mute)' }}> · NPI {p.npi}{p.specialty ? ` · ${p.specialty}` : ''}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={labelStyle} htmlFor="inv-npi">NPI</label>
                <input id="inv-npi" type="text" value={newNpi} onChange={e => setNewNpi(e.target.value)} disabled={submitting} style={inputStyle} placeholder="1234567890" />
              </div>
              <div>
                <label style={labelStyle} htmlFor="inv-pname">Prescriber name</label>
                <input id="inv-pname" type="text" value={newPrescName} onChange={e => setNewPrescName(e.target.value)} disabled={submitting} style={inputStyle} placeholder="Dr. Jane Smith" />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle} htmlFor="inv-spec">Specialty (optional)</label>
                <input id="inv-spec" type="text" value={newSpecialty} onChange={e => setNewSpecialty(e.target.value)} disabled={submitting} style={inputStyle} placeholder="Cardiology" />
              </div>
              <div style={{ gridColumn: '1 / -1', fontSize: 12, color: 'var(--color-text-mute)' }}>
                If a prescriber with this NPI already exists, we’ll link to it instead of creating a duplicate.
              </div>
            </div>
          )}
        </div>
      )}

      {error && (
        <div role="alert" style={errorStyle}>
          <AlertCircle size={16} aria-hidden="true" /> {error}
        </div>
      )}

      <div style={{ marginTop: 16, padding: '12px 14px', border: '1px solid var(--color-border)', borderRadius: 6, background: 'var(--color-surface)' }}>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={requireMfa}
            onChange={e => setRequireMfa(e.target.checked)}
            disabled={submitting}
            style={{ marginTop: 2 }}
          />
          <span>
            <span style={{ fontSize: 13, fontWeight: 500 }}>Require multi-factor authentication (MFA)</span>
            <span style={{ display: 'block', fontSize: 12, color: 'var(--color-text-mute)', marginTop: 2, lineHeight: 1.4 }}>
              When enabled, this user must set up an authenticator app and enter a code at every sign-in.
              Recommended for external users.
            </span>
          </span>
        </label>
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
        <button type="button" onClick={onCancel} disabled={submitting} style={ghostBtnStyle}>Cancel</button>
        <button type="submit" disabled={submitting} style={primaryButtonStyle}>
          {submitting ? 'Sending…' : 'Send invitation'}
        </button>
      </div>
    </form>
  )
}

// ----- Bootstrap link panel (shown after invite/resend) -----

function BootstrapLinkPanel({
  email, link, preview, onClose,
}: { email: string; link: string; preview?: EmailPreview; onClose: () => void }) {
  const [copied, setCopied] = useState(false)
  const [copiedEmail, setCopiedEmail] = useState(false)
  const internal = preview?.audience === 'internal'
  async function copy() {
    try { await navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 2000) } catch { /* clipboard unavailable */ }
  }
  async function copyEmail() {
    if (!preview) return
    try { await navigator.clipboard.writeText(`Subject: ${preview.subject}\n\n${preview.body}`); setCopiedEmail(true); setTimeout(() => setCopiedEmail(false), 2000) } catch { /* clipboard unavailable */ }
  }

  return (
    <div style={{ ...bootstrapPanelStyle, flexDirection: 'column', alignItems: 'stretch', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ fontWeight: 600, fontSize: 13, flex: 1 }}>Invitation queued for {email}</div>
        {preview && (
          <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999,
            background: internal ? 'var(--color-accent-soft, #e0f2f1)' : 'var(--color-surface-2, #eef2f1)',
            color: internal ? 'var(--color-accent, #0d9488)' : 'var(--color-text-soft)' }}>
            {internal ? 'Internal employee · SSO' : 'External · password'}
          </span>
        )}
        <button onClick={onClose} style={ghostBtnStyle} aria-label="Dismiss"><X size={14} aria-hidden="true" /></button>
      </div>

      <div style={{ fontSize: 11, color: 'var(--color-text-soft)' }}>
        Email delivery is stubbed — this is exactly what the invitee will receive. Copy the message below and send it to them, or copy the link directly.
      </div>

      {preview && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Email preview</span>
            <button onClick={copyEmail} style={{ ...ghostBtnStyle, fontSize: 11, padding: '2px 8px' }}>
              {copiedEmail ? <Check size={12} aria-hidden="true" /> : <Copy size={12} aria-hidden="true" />}
              <span>{copiedEmail ? 'Copied' : 'Copy email'}</span>
            </button>
          </div>
          <div style={{ fontSize: 11, color: 'var(--color-text-soft)', marginBottom: 2 }}>Subject: {preview.subject}</div>
          <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'ui-monospace, monospace', fontSize: 11, background: 'var(--color-surface-2, #f6f8f8)',
            border: '1px solid var(--color-border)', borderRadius: 8, padding: 10, margin: 0, maxHeight: 220, overflow: 'auto' }}>{preview.body}</pre>
        </div>
      )}

      {/* The set-password link only applies to external (password) invitees; internal employees sign in via Employee login. */}
      {!internal && (
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Set-password link</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input type="text" value={link} readOnly onFocus={e => e.target.select()}
              style={{ ...inputStyle, fontSize: 11, fontFamily: 'ui-monospace, monospace', flex: 1 }} />
            <button onClick={copy} style={ghostBtnStyle}>
              {copied ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
              <span>{copied ? 'Copied' : 'Copy'}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ----- Helpers -----

function relTime(iso: string): string {
  const t = new Date(iso).getTime()
  const ms = t - Date.now() // can be negative (past) or positive (future)
  const absMs = Math.abs(ms)
  const future = ms > 0
  const m = Math.floor(absMs / 60000)
  if (m < 1)  return future ? 'in <1m' : 'just now'
  if (m < 60) return future ? `in ${m}m` : `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return future ? `in ${h}h` : `${h}h ago`
  const d = Math.floor(h / 24)
  if (d < 30) return future ? `in ${d}d` : `${d}d ago`
  return formatUsDateLocal(iso)
}

// ----- Styles -----

const toolbarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 16,
  flexWrap: 'wrap',
}

const chipRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 6,
  flexWrap: 'wrap',
}

const chipStyle: React.CSSProperties = {
  padding: '6px 12px',
  background: 'var(--color-surface, #fff)',
  border: '1px solid var(--color-border)',
  borderRadius: 14,
  fontSize: 13,
  color: 'var(--color-text-soft)',
  cursor: 'pointer',
  fontFamily: 'inherit',
}

const chipActiveStyle: React.CSSProperties = {
  background: '#1f2937',
  color: '#fff',
  borderColor: '#1f2937',
}

const inviteFormStyle: React.CSSProperties = {
  background: 'var(--color-surface, #fff)',
  border: '1px solid var(--color-border)',
  borderRadius: 8,
  padding: 18,
  marginBottom: 16,
}

const inviteFormGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 14,
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--color-text)',
  marginBottom: 4,
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid var(--color-border)',
  borderRadius: 6,
  fontSize: 13,
  fontFamily: 'inherit',
  background: 'var(--color-surface, #fff)',
}

const roleChipsStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
}

const roleChipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '6px 10px',
  background: 'var(--color-surface-alt)',
  border: '1px solid var(--color-border)',
  borderRadius: 14,
  fontSize: 12,
  cursor: 'pointer',
  userSelect: 'none',
}

const roleChipActiveStyle: React.CSSProperties = {
  background: 'var(--color-primary-soft, #ccfbf1)',
  borderColor: 'var(--color-primary, #0f766e)',
  color: 'var(--color-primary, #0f766e)',
  fontWeight: 600,
}

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
}

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '10px 14px',
  background: 'var(--color-surface-alt)',
  borderBottom: '1px solid var(--color-border)',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: 'var(--color-text-mute)',
}

const trStyle: React.CSSProperties = {
  borderBottom: '1px solid var(--color-border)',
}

const tdStyle: React.CSSProperties = {
  padding: '12px 14px',
  verticalAlign: 'middle',
}

const statusPillStyle: React.CSSProperties = {
  display: 'inline-block',
  padding: '2px 8px',
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 0.4,
  borderRadius: 10,
}

const primaryButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 14px',
  background: 'var(--color-primary, #0f766e)',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'inherit',
}

const ghostBtnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '6px 10px',
  background: 'transparent',
  border: '1px solid var(--color-border)',
  borderRadius: 6,
  fontSize: 12,
  color: 'var(--color-text-soft)',
  cursor: 'pointer',
  fontFamily: 'inherit',
  marginLeft: 4,
}

const dangerBtnStyle: React.CSSProperties = {
  ...ghostBtnStyle,
  borderColor: '#fecaca',
  color: '#991b1b',
}

const emptyStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 10,
  padding: '40px 20px',
  color: 'var(--color-text-mute)',
  fontSize: 13,
}

const errorStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 12px',
  background: '#fee2e2',
  border: '1px solid #fecaca',
  borderRadius: 4,
  color: '#991b1b',
  fontSize: 13,
  marginTop: 8,
}

const bootstrapPanelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  padding: 14,
  background: '#fef3c7',
  border: '1px solid #fbbf24',
  borderRadius: 8,
  marginBottom: 16,
}
